/**
 * Custom React Hook for Billing Claims API
 */

import { useState, useEffect, useCallback } from 'react';
import billingService from '../services/billingService';
import {
  BillingClaimDTO,
  CreateBillingClaimRequest,
  ClaimStatus,
  ClaimType,
  PageResponse,
} from '../types/billing.types';

interface UseBillingProps {
  residentId?: string;
  autoFetch?: boolean;
}

export const useBilling = ({ residentId, autoFetch = true }: UseBillingProps = {}) => {
  const [billingClaims, setBillingClaims] = useState<BillingClaimDTO[]>([]);
  const [currentClaim, setCurrentClaim] = useState<BillingClaimDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = 20;

  // Fetch all billing claims with pagination
  const fetchBillingClaims = useCallback(async (page: number = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response: PageResponse<BillingClaimDTO> = await billingService.getAllBillingClaims(page, pageSize);
      setBillingClaims(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
      setCurrentPage(response.number);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch billing claims');
      console.error('Error fetching billing claims:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch billing claims by resident
  const fetchBillingClaimsByResident = useCallback(async (resId: string) => {
    setLoading(true);
    setError(null);
    try {
      const claims = await billingService.getBillingClaimsByResident(resId);
      setBillingClaims(claims);
      setTotalElements(claims.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch billing claims for resident');
      console.error('Error fetching billing claims by resident:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch billing claims by status
  const fetchBillingClaimsByStatus = useCallback(async (status: ClaimStatus) => {
    setLoading(true);
    setError(null);
    try {
      const claims = await billingService.getBillingClaimsByStatus(status);
      setBillingClaims(claims);
      setTotalElements(claims.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch billing claims by status');
      console.error('Error fetching billing claims by status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch billing claims by type
  const fetchBillingClaimsByType = useCallback(async (claimType: ClaimType) => {
    setLoading(true);
    setError(null);
    try {
      const claims = await billingService.getBillingClaimsByType(claimType);
      setBillingClaims(claims);
      setTotalElements(claims.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch billing claims by type');
      console.error('Error fetching billing claims by type:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch billing claim by ID
  const fetchBillingClaimById = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const claim = await billingService.getBillingClaimById(id);
      setCurrentClaim(claim);
      return claim;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch billing claim');
      console.error('Error fetching billing claim:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch billing claim by claim number
  const fetchBillingClaimByClaimNumber = useCallback(async (claimNumber: string) => {
    setLoading(true);
    setError(null);
    try {
      const claim = await billingService.getBillingClaimByClaimNumber(claimNumber);
      setCurrentClaim(claim);
      return claim;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch billing claim by claim number');
      console.error('Error fetching billing claim by claim number:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new billing claim
  const createBillingClaim = useCallback(async (request: CreateBillingClaimRequest) => {
    setSaving(true);
    setError(null);
    try {
      const newClaim = await billingService.createBillingClaim(request);
      setBillingClaims((prev) => [newClaim, ...prev]);
      setCurrentClaim(newClaim);
      return newClaim;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create billing claim');
      console.error('Error creating billing claim:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  // Update billing claim
  const updateBillingClaim = useCallback(async (id: string, data: Partial<BillingClaimDTO>) => {
    setSaving(true);
    setError(null);
    try {
      const updatedClaim = await billingService.updateBillingClaim(id, data);
      setBillingClaims((prev) =>
        prev.map((claim) => (claim.id === id ? updatedClaim : claim))
      );
      if (currentClaim?.id === id) {
        setCurrentClaim(updatedClaim);
      }
      return updatedClaim;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update billing claim');
      console.error('Error updating billing claim:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentClaim]);

  // Submit billing claim
  const submitBillingClaim = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      const submittedClaim = await billingService.submitBillingClaim(id);
      setBillingClaims((prev) =>
        prev.map((claim) => (claim.id === id ? submittedClaim : claim))
      );
      if (currentClaim?.id === id) {
        setCurrentClaim(submittedClaim);
      }
      return submittedClaim;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit billing claim');
      console.error('Error submitting billing claim:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentClaim]);

  // Approve billing claim
  const approveBillingClaim = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      const approvedClaim = await billingService.approveBillingClaim(id);
      setBillingClaims((prev) =>
        prev.map((claim) => (claim.id === id ? approvedClaim : claim))
      );
      if (currentClaim?.id === id) {
        setCurrentClaim(approvedClaim);
      }
      return approvedClaim;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve billing claim');
      console.error('Error approving billing claim:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentClaim]);

  // Deny billing claim
  const denyBillingClaim = useCallback(async (id: string, denialReason: string, denialCode?: string) => {
    setSaving(true);
    setError(null);
    try {
      const deniedClaim = await billingService.denyBillingClaim(id, denialReason, denialCode);
      setBillingClaims((prev) =>
        prev.map((claim) => (claim.id === id ? deniedClaim : claim))
      );
      if (currentClaim?.id === id) {
        setCurrentClaim(deniedClaim);
      }
      return deniedClaim;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deny billing claim');
      console.error('Error denying billing claim:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentClaim]);

  // Delete billing claim
  const deleteBillingClaim = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      await billingService.deleteBillingClaim(id);
      setBillingClaims((prev) => prev.filter((claim) => claim.id !== id));
      if (currentClaim?.id === id) {
        setCurrentClaim(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete billing claim');
      console.error('Error deleting billing claim:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentClaim]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      if (residentId) {
        fetchBillingClaimsByResident(residentId);
      } else {
        fetchBillingClaims(0);
      }
    }
  }, [residentId, autoFetch, fetchBillingClaims, fetchBillingClaimsByResident]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    fetchBillingClaims(page);
  }, [fetchBillingClaims]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      fetchBillingClaims(currentPage + 1);
    }
  }, [currentPage, totalPages, fetchBillingClaims]);

  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      fetchBillingClaims(currentPage - 1);
    }
  }, [currentPage, fetchBillingClaims]);

  return {
    // State
    billingClaims,
    currentClaim,
    loading,
    saving,
    error,
    totalElements,
    totalPages,
    currentPage,
    pageSize,

    // Actions
    fetchBillingClaims,
    fetchBillingClaimsByResident,
    fetchBillingClaimsByStatus,
    fetchBillingClaimsByType,
    fetchBillingClaimById,
    fetchBillingClaimByClaimNumber,
    createBillingClaim,
    updateBillingClaim,
    submitBillingClaim,
    approveBillingClaim,
    denyBillingClaim,
    deleteBillingClaim,
    handlePageChange,
    nextPage,
    prevPage,
    setCurrentClaim,
    setError,
  };
};
